import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';

// Znaczniki dozwolone we wzorcu numeru - uzywane TYLKO wtedy, gdy backend
// nie odda listy w GET /document-types (starsza wersja API). Zrodlem prawdy
// jest rejestr typow po stronie backendu (utils/document_types.py).
const ZNACZNIKI_ZAPASOWE = ['{prefix}', '{numer}', '{rok}', '{miesiac}'];

// Kody oddzialow uzywane przez backend przy zakladaniu prefiksow
// (_generate_location_code). Frontend musi je znac, zeby wzorzec dodany
// recznie z UI konczyl sie tym samym kodem co wzorce zakladane automatycznie -
// bez kodu oddzialu numery z dwoch sklepow moglyby sie zdublowac.
const KODY_ODDZIALOW = {
    'Szperk': 'SZP',
    'Kalisz': 'KAL',
    'Nysa': 'NYS',
    'Leszno': 'LES',
    'Poznań': 'POZ',
    'Wejherowo': 'WEJ'
};

const DNI_ALERTOW = 30;

const DocumentPrefixesPage = () => {
    const [prefixes, setPrefixes] = useState([]);
    const [locations, setLocations] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [znaczniki, setZnaczniki] = useState(ZNACZNIKI_ZAPASOWE);
    const [locationsWithoutPrefixes, setLocationsWithoutPrefixes] = useState([]);
    const [alerty, setAlerty] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPrefix, setEditingPrefix] = useState(null);
    const [editingOryginal, setEditingOryginal] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
    const [message, setMessage] = useState({ text: '', type: '' });
    // Czy uzytkownik recznie ruszyl pole formatu - jesli nie, podstawiamy
    // kanoniczny wzorzec z kodem oddzialu przy kazdej zmianie lokalizacji.
    const [formatRuszony, setFormatRuszony] = useState(false);
    const [newPrefix, setNewPrefix] = useState({
        location_id: '',
        document_type: '',
        prefix: '',
        format_pattern: '{prefix}/{numer}/{miesiac}/{rok}',
        description: '',
        reset_period: 'monthly',
        active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    // Komunikat bledu z backendu (400 niesie tresc w body), a nie suchy
    // 'Request failed with status code 400' z axiosa.
    const komunikatBledu = (error) => (
        error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || 'Nieznany błąd'
    );

    // Zapytania poboczne NIE MOGA wywrocic calej zakladki. Gdyby np. endpoint
    // alertow nie istnial (starszy backend), Promise.all odrzucilby komplet
    // danych i lista 80+ prefiksow zniknelaby z ekranu - dokladnie tak wygladal
    // wczesniejszy blad z `res.ok`.
    const pobierzOpcjonalnie = (url) => api.get(url).catch((error) => {
        console.warn(`Pomijam ${url}: ${komunikatBledu(error)}`);
        return { status: 204, data: { data: [] } };
    });

    const fetchData = async () => {
        try {
            setLoading(true);

            const [prefixesRes, locationsRes, typesRes, withoutRes, alertyRes] = await Promise.all([
                api.get('/document-prefixes'),
                api.get('/locations'),
                pobierzOpcjonalnie('/document-types'),
                pobierzOpcjonalnie('/locations-without-prefixes'),
                pobierzOpcjonalnie('/numeracja-alerty')
            ]);

            // UWAGA: to jest axios, NIE fetch. Odpowiedz axiosa nie ma pola `.ok`
            // (ma `status`), wiec warunek `res.ok ? ... : { data: [] }` wychodzil
            // ZAWSZE falsywy i cala zakladka renderowala sie pusta, mimo ze
            // wszystkie zapytania konczyly sie kodem 200.
            const zData = (res) => {
                const ok = res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;
                return ok && res.data ? res.data : { data: [] };
            };

            const prefixesData = zData(prefixesRes);
            const locationsData = zData(locationsRes);
            const typesData = zData(typesRes);
            const withoutData = zData(withoutRes);
            const alertyData = zData(alertyRes);

            const listaPrefiksow = prefixesData.data || [];
            setPrefixes(listaPrefiksow);
            setLocations(locationsData.data || []);
            setDocumentTypes(typesData.data || []);
            setZnaczniki(
                Array.isArray(typesData.znaczniki) && typesData.znaczniki.length
                    ? typesData.znaczniki
                    : ZNACZNIKI_ZAPASOWE
            );
            setLocationsWithoutPrefixes(withoutData.data || []);
            setAlerty(alertyData.data || []);

            return listaPrefiksow;
        } catch (error) {
            console.error('Błąd pobierania danych:', error);
            setMessage({ text: `Błąd: ${komunikatBledu(error)}`, type: 'error' });
            return null;
        } finally {
            setLoading(false);
        }
    };

    // === TYPY DOKUMENTOW ===
    // Etykiety pochodza WYLACZNIE z GET /document-types (rejestr backendu).
    // Wczesniej byla tu twarda mapa - piata kopia listy typow w systemie;
    // wlasnie rozjazd tych kopii wyprodukowal typy-widma ('pw', 'rw').
    const etykietyTypow = useMemo(() => {
        const mapa = {};
        documentTypes.forEach((t) => { mapa[t.type] = t.name; });
        return mapa;
    }, [documentTypes]);

    // Typ spoza rejestru = wiersz po wycofanym typie (np. dawne 'zwrot_paragonu').
    // Oznaczamy go, zeby administrator nie konfigurowal numeracji, ktorej
    // zaden dokument nie uzywa. Gdy rejestr jest niedostepny, pokazujemy
    // sam klucz - inaczej WSZYSTKO wygladaloby na nieuzywane.
    const getDocumentTypeLabel = (type) => {
        if (etykietyTypow[type]) return etykietyTypow[type];
        return documentTypes.length ? `${type} (nieużywany)` : type;
    };

    // === WALIDACJA WZORCA (lustro waliduj_wzorzec z backendu) ===
    const walidujWzorzec = (wzorzec, resetPeriod) => {
        const tekst = (wzorzec || '').trim();
        if (!tekst) return 'Wzorzec numeru nie może być pusty';

        const otwarte = (tekst.match(/{/g) || []).length;
        const zamkniete = (tekst.match(/}/g) || []).length;
        if (otwarte !== zamkniete) {
            return 'Błędny wzorzec numeru - niedomknięty nawias klamrowy';
        }

        // PODWOJNE NAWIASY. Backend podstawia znaczniki przez .format(), ktore
        // '{{numer}}' zamienia na doslowny tekst '{numer}' i NIE zglasza bledu -
        // kazdy dokument dostaje wtedy ten sam ciag, a licznik rosnie w tle.
        // Zwykly test "zawiera {numer}" tego nie widzi, bo '{{numer}}' zawiera
        // podciag '{numer}'. To jest dokladnie ten literal, ktory trafial do bazy
        // przy zakladaniu prefiksow nowej lokalizacji.
        if (tekst.includes('{{') || tekst.includes('}}')) {
            return 'Wzorzec ma podwójne nawiasy klamrowe - znacznik nie zostanie podstawiony. Użyj pojedynczych, np. {numer}';
        }

        const uzyte = tekst.match(/{[^{}]*}/g) || [];
        const nieznany = uzyte.find((z) => !znaczniki.includes(z));
        if (nieznany) {
            return `Nieznany znacznik ${nieznany} - dozwolone: ${znaczniki.join(', ')}`;
        }
        if (!tekst.includes('{numer}')) {
            return 'Wzorzec musi zawierać znacznik {numer} - inaczej każdy dokument dostanie ten sam numer';
        }
        if (resetPeriod === 'monthly' && !tekst.includes('{miesiac}')) {
            return 'Reset miesięczny wymaga znacznika {miesiac} - bez niego numery powtarzają się co miesiąc';
        }
        if (resetPeriod === 'yearly' && !tekst.includes('{rok}')) {
            return 'Reset roczny wymaga znacznika {rok} - bez niego numery powtarzają się co rok';
        }
        return null;
    };

    const podgladNumeru = (wzorzec, prefiks) => {
        const teraz = new Date();
        return (wzorzec || '')
            .replace(/{prefix}/g, prefiks || 'XXX')
            .replace(/{numer}/g, '0001')
            .replace(/{rok}/g, String(teraz.getFullYear()))
            .replace(/{miesiac}/g, String(teraz.getMonth() + 1).padStart(2, '0'));
    };

    // Kod oddzialu doklejany na koncu wzorca. Najpierw probujemy odczytac go
    // z istniejacego prefiksu tej lokalizacji (to dokladnie ten kod, ktorego
    // uzyl backend), a dopiero potem wyliczamy go z nazwy lokalizacji.
    const kodOddzialu = (locationId) => {
        const wlasne = prefixes.filter((p) => String(p.location_id) === String(locationId));
        for (const p of wlasne) {
            const czlony = String(p.format_pattern || '').split('/');
            const ostatni = czlony[czlony.length - 1];
            if (ostatni && !ostatni.includes('{') && /^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż0-9-]{2,6}$/.test(ostatni)) {
                return ostatni;
            }
        }
        const lokalizacja = locations.find((l) => String(l.id) === String(locationId));
        const nazwa = String(lokalizacja?.nazwa || lokalizacja?.name || '').replace('SFD ', '').trim();
        if (!nazwa) return null;
        return KODY_ODDZIALOW[nazwa] || nazwa.slice(0, 3).toUpperCase();
    };

    const wzorzecDomyslny = (locationId) => {
        const kod = kodOddzialu(locationId);
        return kod
            ? `{prefix}/{numer}/{miesiac}/{rok}/${kod}`
            : '{prefix}/{numer}/{miesiac}/{rok}';
    };

    // === ALERTY NUMERACJI ===
    const parsujDate = (wartosc) => {
        if (!wartosc) return null;
        const bezposrednio = new Date(wartosc);
        if (!Number.isNaN(bezposrednio.getTime())) return bezposrednio;
        const zamiana = new Date(String(wartosc).replace(' ', 'T'));
        return Number.isNaN(zamiana.getTime()) ? null : zamiana;
    };

    const formatujDate = (wartosc) => {
        const data = parsujDate(wartosc);
        return data ? data.toLocaleString('pl-PL') : '-';
    };

    // Grupowanie alertow po lokalizacji i typie dokumentu - administrator ma
    // zobaczyc, GDZIE brakuje konfiguracji, a nie 50 pojedynczych wpisow.
    const alertyPodsumowanie = useMemo(() => {
        const granica = Date.now() - DNI_ALERTOW * 24 * 60 * 60 * 1000;
        const mapa = new Map();

        (alerty || []).forEach((a) => {
            const data = parsujDate(a.created_at);
            if (data && data.getTime() < granica) return;

            const klucz = `${a.location_id}|${a.document_type}`;
            const wpis = mapa.get(klucz);
            if (wpis) {
                wpis.liczba += 1;
                if (!wpis.data || (data && data.getTime() > wpis.data.getTime())) {
                    wpis.data = data;
                    wpis.numer = a.numer_awaryjny;
                    wpis.powod = a.powod;
                }
            } else {
                mapa.set(klucz, {
                    klucz,
                    location_id: a.location_id,
                    location_name: a.location_name || (a.location_id ? `Lokalizacja ${a.location_id}` : 'brak lokalizacji'),
                    document_type: a.document_type,
                    liczba: 1,
                    data,
                    numer: a.numer_awaryjny,
                    powod: a.powod
                });
            }
        });

        return Array.from(mapa.values()).sort((a, b) => b.liczba - a.liczba);
    }, [alerty]);

    // === AKCJE ===
    const createDefaultPrefixesForLocation = async (locationId) => {
        try {
            const res = await api.post(`/create-default-prefixes/${locationId}`);
            setMessage({ text: res?.data?.message || 'Utworzono domyślne prefiksy', type: 'success' });
            fetchData();
        } catch (error) {
            setMessage({ text: komunikatBledu(error), type: 'error' });
        }
    };

    const dodajBrakujaceTypy = async (locationId) => {
        try {
            const res = await api.post(`/add-missing-prefixes/${locationId}`);
            setMessage({ text: res?.data?.message || 'Uzupełniono brakujące prefiksy', type: 'success' });
            fetchData();
        } catch (error) {
            setMessage({ text: komunikatBledu(error), type: 'error' });
        }
    };

    // Wspolny zapis prefiksu. oczekiwanyLicznik != null oznacza, ze zmieniamy
    // licznik - po zapisie sprawdzamy, czy backend faktycznie go zapisal
    // (zamiast po cichu udawac, ze korekta sie udala).
    const zapiszPrefiks = async (prefiks, oczekiwanyLicznik = null) => {
        const blad = walidujWzorzec(prefiks.format_pattern, prefiks.reset_period);
        if (blad) {
            setMessage({ text: blad, type: 'error' });
            return false;
        }

        try {
            await api.put(`/document-prefixes/${prefiks.id}`, prefiks);
            const swieze = await fetchData();

            if (oczekiwanyLicznik !== null && Array.isArray(swieze)) {
                const wiersz = swieze.find((p) => p.id === prefiks.id);
                if (wiersz && Number(wiersz.current_number || 0) !== Number(oczekiwanyLicznik)) {
                    // Kontrola zostaje mimo poprawki po stronie backendu (PUT
                    // /document-prefixes zapisuje juz current_number): korekta
                    // licznika to zdarzenie ksiegowe i nie wolno jej udawac,
                    // gdy z jakiegokolwiek powodu nie doszla do skutku.
                    setMessage({
                        text: 'Prefiks zapisany, ale LICZNIK NIE ZOSTAŁ ZMIENIONY '
                            + `(w bazie nadal ${wiersz.current_number}). `
                            + 'Numeracja działa dalej od poprzedniej wartości - '
                            + 'sprawdź uprawnienia i log serwera.',
                        type: 'error'
                    });
                    return false;
                }
            }

            setMessage({ text: 'Prefiks zaktualizowany', type: 'success' });
            return true;
        } catch (error) {
            setMessage({ text: komunikatBledu(error), type: 'error' });
            return false;
        }
    };

    const handleUpdatePrefix = async () => {
        if (!editingPrefix) return;

        const licznikPrzed = Number(editingOryginal?.current_number || 0);
        const licznikPo = Number(editingPrefix.current_number);

        if (!Number.isInteger(licznikPo) || licznikPo < 0) {
            setMessage({ text: 'Licznik musi być liczbą całkowitą nie mniejszą niż 0', type: 'error' });
            return;
        }

        if (licznikPo !== licznikPrzed) {
            const nastepny = licznikPo + 1;
            const potwierdzenie = window.confirm(
                'Zmieniasz licznik dokumentów.\n\n'
                + `Było: ${licznikPrzed}\nBędzie: ${licznikPo}\n\n`
                + `Następny dokument tego typu dostanie numer ${nastepny}.\n`
                + 'Ustawienie licznika PONIŻEJ już wystawionych numerów spowoduje duplikaty.\n\n'
                + 'Kontynuować?'
            );
            if (!potwierdzenie) return;
            // Slad w konsoli przegladarki; zapis do dziennika po stronie
            // serwera nalezy do endpointu aktualizacji prefiksu.
            console.warn(
                `[numeracja] korekta licznika prefiksu id=${editingPrefix.id} `
                + `(${editingPrefix.document_type}, lokalizacja ${editingPrefix.location_id}): `
                + `${licznikPrzed} -> ${licznikPo}`
            );
        }

        const ok = await zapiszPrefiks(
            { ...editingPrefix, current_number: licznikPo },
            licznikPo !== licznikPrzed ? licznikPo : null
        );
        if (ok) {
            setEditingPrefix(null);
            setEditingOryginal(null);
        }
    };

    const handleCreatePrefix = async () => {
        const blad = walidujWzorzec(newPrefix.format_pattern, newPrefix.reset_period);
        if (blad) {
            setMessage({ text: blad, type: 'error' });
            return;
        }

        try {
            const res = await api.post('/document-prefixes', newPrefix);
            setMessage({ text: res?.data?.message || 'Prefiks utworzony', type: 'success' });
            setShowCreateModal(false);
            setFormatRuszony(false);
            setNewPrefix({
                location_id: '',
                document_type: '',
                prefix: '',
                format_pattern: '{prefix}/{numer}/{miesiac}/{rok}',
                description: '',
                // Ten sam okres co stan poczatkowy formularza - wczesniej byl
                // tu 'yearly' i drugi prefiks zakladany w tej samej sesji
                // po cichu dostawal reset roczny zamiast miesiecznego.
                reset_period: 'monthly',
                active: true
            });
            fetchData();
        } catch (error) {
            setMessage({ text: komunikatBledu(error), type: 'error' });
        }
    };

    const handleResetCounter = async (prefix) => {
        if (!window.confirm(
            `Wyzerować licznik dla: ${getDocumentTypeLabel(prefix.document_type)}?\n\n`
            + `Obecna wartość: ${prefix.current_number || 0}\n`
            + 'Następny dokument dostanie numer 1 - jeśli taki numer już był wystawiony w tym okresie, powstanie duplikat.'
        )) {
            return;
        }
        console.warn(
            `[numeracja] zerowanie licznika prefiksu id=${prefix.id} `
            + `(${prefix.document_type}, lokalizacja ${prefix.location_id})`
        );
        await zapiszPrefiks({ ...prefix, current_number: 0 }, 0);
    };

    const handleMigrateToNewFormat = async () => {
        if (!window.confirm('Czy na pewno chcesz zmigrować wszystkie prefiksy do nowego formatu?\n\nNowy format: TYP/numer/miesiąc/rok/kod_magazynu\nNp.: KP/0001/02/2026/OLE\n\nTa operacja zaktualizuje wszystkie istniejące prefiksy.')) {
            return;
        }

        try {
            setLoading(true);
            const response = await api.post('/migrate-to-new-format');
            setMessage({ text: response?.data?.message || 'Zmigrowano prefiksy', type: 'success' });
            fetchData();
        } catch (error) {
            setMessage({ text: komunikatBledu(error), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const otworzEdycje = (prefix) => {
        setEditingPrefix({ ...prefix, current_number: Number(prefix.current_number || 0) });
        setEditingOryginal({ ...prefix });
    };

    const zamknijEdycje = () => {
        setEditingPrefix(null);
        setEditingOryginal(null);
    };

    // Grupowanie prefiksów po lokalizacji
    const groupedPrefixes = prefixes.reduce((acc, prefix) => {
        const locId = prefix.location_id;
        if (!acc[locId]) {
            acc[locId] = {
                location_id: locId,
                location_name: prefix.location_name || `Lokalizacja ${locId}`,
                prefixes: []
            };
        }
        acc[locId].prefixes.push(prefix);
        return acc;
    }, {});

    const filteredGroupedPrefixes = selectedLocationFilter
        ? { [selectedLocationFilter]: groupedPrefixes[selectedLocationFilter] }
        : groupedPrefixes;

    // Typy z rejestru, ktorych dana lokalizacja jeszcze nie ma - to wlasnie
    // one skazuja dokumenty na numer awaryjny.
    const brakujaceTypy = (group) => {
        const posiadane = new Set(group.prefixes.map((p) => p.document_type));
        return documentTypes.filter((t) => !posiadane.has(t.type));
    };

    // Inline styles - kompaktowe, wylacznie tokeny kolorow
    const styles = {
        container: {
            padding: '0.75rem',
            maxWidth: '1400px',
            margin: '0 auto',
            fontSize: '12px'
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
            paddingBottom: '0.5rem',
            borderBottom: '1px solid var(--border)'
        },
        title: {
            margin: 0,
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--text)'
        },
        notka: {
            fontSize: '11px',
            color: 'var(--text-secondary)',
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            padding: '6px 12px',
            marginBottom: '0.5rem'
        },
        message: {
            padding: '6px 12px',
            borderRadius: '4px',
            marginBottom: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px'
        },
        successMessage: {
            backgroundColor: 'var(--success-light)',
            color: 'var(--success)',
            border: '1px solid var(--success-light)'
        },
        errorMessage: {
            backgroundColor: 'var(--danger-light)',
            color: 'var(--danger)',
            border: '1px solid var(--danger-light)'
        },
        alertCard: {
            backgroundColor: 'var(--danger-light)',
            border: '1px solid var(--danger)',
            borderRadius: '4px',
            padding: '6px 12px',
            marginBottom: '0.75rem'
        },
        alertTitle: {
            color: 'var(--danger)',
            fontWeight: '600',
            fontSize: '12px',
            marginBottom: '0.2rem'
        },
        alertNote: {
            color: 'var(--text-secondary)',
            fontSize: '11px',
            marginBottom: '0.4rem'
        },
        okNote: {
            color: 'var(--text-muted)',
            fontSize: '11px',
            marginBottom: '0.5rem'
        },
        filterBar: {
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            marginBottom: '0.75rem',
            padding: '0.5rem',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px',
            border: '1px solid var(--border)',
            fontSize: '12px'
        },
        select: {
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '12px',
            minWidth: '150px',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text)'
        },
        button: {
            padding: '6px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.2rem',
            transition: 'all 0.2s'
        },
        primaryButton: {
            backgroundColor: 'var(--info)',
            color: '#fff'
        },
        successButton: {
            backgroundColor: 'var(--success)',
            color: '#fff'
        },
        dangerButton: {
            backgroundColor: 'var(--danger)',
            color: '#fff'
        },
        warningButton: {
            backgroundColor: 'var(--warning)',
            // Atrament STALY, a nie var(--text): token --warning jest jasnym
            // bursztynem w OBU motywach (#f59e0b / #fbbf24), a --text odwraca
            // sie na jasny w ciemnym motywie - napis na przycisku mial wtedy
            // kontrast 1,5:1 i byl praktycznie niewidoczny.
            // Kontrast granatu #0f172a: 7,5:1 (jasny motyw), 10,1:1 (ciemny).
            color: '#0f172a'
        },
        // Przycisk zamkniecia "x". MUSI miec wlasny kolor: uniwersalna regula
        // motywu (v3sys-theme.css) pomija przyciski, ktorych atrybut style
        // zawiera 'background', wiec bez tego znak zostawal czarny takze
        // w ciemnym motywie (kontrast 1,4:1 na tle karty modala).
        closeButton: {
            background: 'none',
            border: 'none',
            color: 'var(--text-secondary)',
            fontSize: '12px',
            cursor: 'pointer',
            padding: '4px 10px'
        },
        locationCard: {
            backgroundColor: 'var(--card-bg)',
            borderRadius: '4px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            marginBottom: '0.75rem',
            overflow: 'hidden',
            border: '1px solid var(--border)'
        },
        locationHeader: {
            padding: '4px 10px',
            // Bylo: tlo var(--text-secondary) + bialy tekst. W ciemnym motywie
            // --text-secondary to JASNY szary (#cbd5e1), wiec nazwa sklepu
            // miala kontrast 1,5:1 - admin nie widzial, ktorej lokalizacji
            // prefiksy edytuje. --card-bg-hover odcina sie od karty w obu
            // motywach, a --text jest do niego dobrany z definicji.
            backgroundColor: 'var(--card-bg-hover)',
            color: 'var(--text)',
            borderBottom: '1px solid var(--border)',
            fontSize: '12px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px'
        },
        th: {
            padding: '4px 10px',
            textAlign: 'left',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            fontWeight: '600',
            color: 'var(--text-secondary)',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.3px'
        },
        td: {
            padding: '4px 10px',
            borderBottom: '1px solid var(--border)',
            verticalAlign: 'middle'
        },
        badge: {
            padding: '2px 8px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: '600'
        },
        activeBadge: {
            backgroundColor: 'var(--success-light)',
            color: 'var(--success)'
        },
        inactiveBadge: {
            backgroundColor: 'var(--danger-light)',
            color: 'var(--danger)'
        },
        missingBadge: {
            backgroundColor: 'var(--warning)',
            // Ten sam powod co przy warningButton - patrz komentarz wyzej.
            color: '#0f172a',
            padding: '2px 8px',
            borderRadius: '8px',
            fontSize: '10px',
            fontWeight: '600'
        },
        counterBadge: {
            backgroundColor: 'var(--info-light)',
            color: 'var(--info)',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: '600',
            fontFamily: 'monospace',
            fontSize: '12px'
        },
        prefixBadge: {
            backgroundColor: 'var(--warning-light)',
            color: 'var(--warning)',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: '600',
            fontFamily: 'monospace',
            fontSize: '11px'
        },
        formatBadge: {
            backgroundColor: 'var(--border)',
            color: 'var(--text)',
            padding: '2px 8px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '11px'
        },
        actionButtons: {
            display: 'flex',
            gap: '0.25rem'
        },
        smallButton: {
            padding: '4px 10px',
            fontSize: '11px'
        },
        missingCard: {
            backgroundColor: 'var(--warning-light)',
            border: '1px solid var(--warning)',
            borderRadius: '4px',
            padding: '6px 12px',
            marginBottom: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px'
        },
        modal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        },
        modalContent: {
            backgroundColor: 'var(--card-bg)',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '420px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
        },
        modalHeader: {
            padding: '6px 12px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        modalBody: {
            padding: '0.8rem'
        },
        modalFooter: {
            padding: '6px 12px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            backgroundColor: 'var(--bg)'
        },
        formGroup: {
            marginBottom: '0.6rem'
        },
        label: {
            display: 'block',
            marginBottom: '0.2rem',
            fontWeight: '500',
            fontSize: '11px',
            color: 'var(--text-secondary)'
        },
        input: {
            width: '100%',
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            fontSize: '12px',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text)',
            boxSizing: 'border-box'
        },
        hint: {
            display: 'block',
            color: 'var(--text-muted)',
            fontSize: '10px',
            marginTop: '2px'
        },
        hintError: {
            display: 'block',
            color: 'var(--danger)',
            fontSize: '10px',
            marginTop: '2px'
        }
    };

    // Podpowiedz znacznikow - IDENTYCZNA w edycji i w oknie nowego prefiksu.
    const PodpowiedzWzorca = ({ wzorzec, prefiks, resetPeriod }) => {
        const blad = walidujWzorzec(wzorzec, resetPeriod);
        return (
            <>
                <small style={styles.hint}>
                    Dozwolone znaczniki: {znaczniki.join(', ')}
                </small>
                {blad
                    ? <small style={styles.hintError}>⚠️ {blad}</small>
                    : <small style={styles.hint}>Przykład numeru: {podgladNumeru(wzorzec, prefiks)}</small>}
            </>
        );
    };

    if (loading) {
        return (
            <div style={{ ...styles.container, textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '16px', marginBottom: '0.5rem' }}>⏳</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Ładowanie...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Nagłówek */}
            <div style={styles.header}>
                <h1 style={styles.title}>🏷️ Prefiksy dokumentów</h1>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        style={{ ...styles.button, ...styles.warningButton }}
                        onClick={handleMigrateToNewFormat}
                        title="Migruj do formatu: TYP/numer/miesiąc/rok/kod_magazynu"
                    >
                        🔄 Migruj format
                    </button>
                    <button
                        style={{ ...styles.button, ...styles.primaryButton }}
                        onClick={() => {
                            setFormatRuszony(false);
                            setShowCreateModal(true);
                        }}
                    >
                        ➕ Dodaj
                    </button>
                </div>
            </div>

            {/* Notka: to jedyne miejsce konfiguracji numeracji */}
            <div style={styles.notka}>
                ℹ️ To jedyne miejsce konfiguracji numerów dokumentów w systemie. Zakładka „Definicje” została usunięta,
                a „Szablony” odpowiadają wyłącznie za wygląd wydruku - nie za numerację.
            </div>

            {/* Komunikaty */}
            {message.text && (
                <div style={{
                    ...styles.message,
                    ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
                }}>
                    <span>{message.text}</span>
                    <button
                        onClick={() => setMessage({ text: '', type: '' })}
                        style={styles.closeButton}
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Dokumenty z numerem awaryjnym */}
            {alertyPodsumowanie.length > 0 ? (
                <div style={styles.alertCard}>
                    <div style={styles.alertTitle}>
                        🚨 Dokumenty z numerem awaryjnym (ostatnie {DNI_ALERTOW} dni)
                    </div>
                    <div style={styles.alertNote}>
                        Numer awaryjny oznacza, że dokument powstał POZA ciągiem numeracji - prefiks dla tej lokalizacji
                        i typu nie istniał albo miał błędny wzorzec. Uzupełnij konfigurację poniżej, żeby kolejne
                        dokumenty wróciły do ciągu.
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Lokalizacja</th>
                                    <th style={styles.th}>Typ dokumentu</th>
                                    <th style={styles.th}>Liczba</th>
                                    <th className="col-hide-mobile" style={styles.th}>Ostatni numer</th>
                                    <th className="col-hide-mobile" style={styles.th}>Ostatnio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alertyPodsumowanie.map((a) => (
                                    <tr key={a.klucz}>
                                        <td style={styles.td}>{a.location_name}</td>
                                        <td style={styles.td} title={a.powod || ''}>
                                            {getDocumentTypeLabel(a.document_type)}
                                        </td>
                                        <td style={styles.td}>
                                            <span style={{ ...styles.badge, ...styles.inactiveBadge }}>{a.liczba}</span>
                                        </td>
                                        <td className="col-hide-mobile" style={styles.td}>
                                            <span style={styles.formatBadge}>{a.numer || '-'}</span>
                                        </td>
                                        <td className="col-hide-mobile" style={styles.td}>
                                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                                {formatujDate(a.data)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div style={styles.okNote}>
                    ✓ Brak dokumentów z numerem awaryjnym w ostatnich {DNI_ALERTOW} dniach.
                </div>
            )}

            {/* Lokalizacje bez prefiksów */}
            {locationsWithoutPrefixes.length > 0 && (
                <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ color: 'var(--warning)', marginBottom: '0.4rem', fontSize: '12px', fontWeight: '600' }}>
                        ⚠️ Lokalizacje bez prefiksów - dokumenty dostaną numer awaryjny
                    </div>
                    {locationsWithoutPrefixes.map(location => (
                        <div key={location.id} style={styles.missingCard}>
                            <div>
                                <strong>{location.nazwa || location.name}</strong>
                                <span style={{ color: 'var(--text-muted)', marginLeft: '0.3rem' }}>
                                    {location.kod_lokalizacji || location.miasto || location.city || ''}
                                </span>
                            </div>
                            <button
                                style={{ ...styles.button, ...styles.warningButton }}
                                onClick={() => createDefaultPrefixesForLocation(location.id)}
                            >
                                🔧 Utwórz komplet
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Filtrowanie */}
            <div style={styles.filterBar}>
                <span style={{ fontWeight: '500' }}>📍</span>
                <select
                    style={styles.select}
                    value={selectedLocationFilter}
                    onChange={(e) => setSelectedLocationFilter(e.target.value)}
                >
                    <option value="">Wszystkie</option>
                    {locations.map(location => (
                        <option key={location.id} value={location.id}>
                            {location.nazwa || location.name}
                        </option>
                    ))}
                </select>
                {selectedLocationFilter && (
                    <button
                        style={{ ...styles.button, ...styles.dangerButton, ...styles.smallButton }}
                        onClick={() => setSelectedLocationFilter('')}
                    >
                        ✕
                    </button>
                )}
                <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '11px' }}>
                    <strong>{prefixes.length}</strong> prefiksów / <strong>{documentTypes.length}</strong> typów w rejestrze
                </span>
            </div>

            {/* Lista prefiksów pogrupowana po lokalizacjach */}
            {Object.entries(filteredGroupedPrefixes).map(([locId, group]) => group && (
                <div key={locId} style={styles.locationCard}>
                    <div style={styles.locationHeader}>
                        <span>🏪 {group.location_name}</span>
                        {brakujaceTypy(group).length > 0 && (
                            <>
                                <span
                                    style={styles.missingBadge}
                                    title={`Brakuje: ${brakujaceTypy(group).map(t => t.name).join(', ')}`}
                                >
                                    ⚠️ brak {brakujaceTypy(group).length} typów
                                </span>
                                <button
                                    style={{ ...styles.button, ...styles.warningButton, ...styles.smallButton }}
                                    onClick={() => dodajBrakujaceTypy(group.location_id)}
                                >
                                    ➕ Uzupełnij
                                </button>
                            </>
                        )}
                    </div>
                    <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Typ</th>
                                <th style={styles.th}>Prefiks</th>
                                <th className="col-hide-mobile" style={styles.th}>Format</th>
                                <th style={styles.th}>Nr</th>
                                <th className="col-hide-mobile" style={styles.th}>Reset</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.prefixes.map(prefix => (
                                <tr key={prefix.id}>
                                    <td style={styles.td}>
                                        {getDocumentTypeLabel(prefix.document_type)}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.prefixBadge}>
                                            {prefix.prefix}
                                        </span>
                                    </td>
                                    <td className="col-hide-mobile" style={styles.td}>
                                        <span style={styles.formatBadge}>
                                            {prefix.format_pattern}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.counterBadge}>
                                            {prefix.current_number || 0}
                                        </span>
                                    </td>
                                    <td className="col-hide-mobile" style={styles.td}>
                                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                            {prefix.reset_period === 'yearly' ? '📅 rocznie' :
                                             prefix.reset_period === 'monthly' ? '📆 miesięcznie' :
                                             '∞ nigdy'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.badge,
                                            ...(prefix.active ? styles.activeBadge : styles.inactiveBadge)
                                        }}>
                                            {prefix.active ? '✓' : '✗'}
                                        </span>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={styles.actionButtons}>
                                            <button
                                                style={{ ...styles.button, ...styles.primaryButton, ...styles.smallButton }}
                                                onClick={() => otworzEdycje(prefix)}
                                                title="Edytuj prefiks i licznik"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                style={{ ...styles.button, ...styles.warningButton, ...styles.smallButton }}
                                                onClick={() => handleResetCounter(prefix)}
                                                title="Wyzeruj licznik"
                                            >
                                                🔄
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            ))}

            {Object.keys(filteredGroupedPrefixes).length === 0 && (
                <div style={{
                    textAlign: 'center',
                    padding: '1.5rem',
                    color: 'var(--text-muted)',
                    backgroundColor: 'var(--bg)',
                    borderRadius: '4px',
                    fontSize: '12px'
                }}>
                    <div style={{ fontSize: '16px', marginBottom: '0.5rem' }}>📭</div>
                    <div>Brak prefiksów</div>
                </div>
            )}

            {/* Modal edycji */}
            {editingPrefix && (
                <div style={styles.modal} onClick={zamknijEdycje}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: '12px' }}>✏️ Edycja prefiksu</h3>
                            <button
                                onClick={zamknijEdycje}
                                style={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Typ dokumentu</label>
                                <input
                                    style={{ ...styles.input, backgroundColor: 'var(--bg)' }}
                                    value={getDocumentTypeLabel(editingPrefix.document_type)}
                                    disabled
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Prefiks</label>
                                <input
                                    style={styles.input}
                                    value={editingPrefix.prefix || ''}
                                    onChange={(e) => setEditingPrefix({ ...editingPrefix, prefix: e.target.value })}
                                    placeholder="np. PAR"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Format numeru</label>
                                <input
                                    style={styles.input}
                                    value={editingPrefix.format_pattern || ''}
                                    onChange={(e) => setEditingPrefix({ ...editingPrefix, format_pattern: e.target.value })}
                                    placeholder="{prefix}/{numer}/{miesiac}/{rok}"
                                />
                                <PodpowiedzWzorca
                                    wzorzec={editingPrefix.format_pattern}
                                    prefiks={editingPrefix.prefix}
                                    resetPeriod={editingPrefix.reset_period}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Licznik (ostatni nadany numer)</label>
                                <input
                                    style={styles.input}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={editingPrefix.current_number}
                                    onChange={(e) => setEditingPrefix({
                                        ...editingPrefix,
                                        current_number: e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                                    })}
                                />
                                <small style={styles.hint}>
                                    Następny dokument dostanie numer {Number(editingPrefix.current_number || 0) + 1}.
                                    Korygować wyłącznie po imporcie danych lub przywróceniu kopii - ustawienie wartości
                                    poniżej już wystawionych numerów tworzy duplikaty.
                                </small>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Opis</label>
                                <input
                                    style={styles.input}
                                    value={editingPrefix.description || ''}
                                    onChange={(e) => setEditingPrefix({ ...editingPrefix, description: e.target.value })}
                                    placeholder="Opis"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Reset licznika</label>
                                <select
                                    style={styles.input}
                                    value={editingPrefix.reset_period || 'monthly'}
                                    onChange={(e) => setEditingPrefix({ ...editingPrefix, reset_period: e.target.value })}
                                >
                                    <option value="monthly">Miesięcznie</option>
                                    <option value="yearly">Rocznie</option>
                                    <option value="never">Nigdy</option>
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', fontSize: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={!!editingPrefix.active}
                                        onChange={(e) => setEditingPrefix({ ...editingPrefix, active: e.target.checked })}
                                    />
                                    Aktywny
                                </label>
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                style={{ ...styles.button, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
                                onClick={zamknijEdycje}
                            >
                                Anuluj
                            </button>
                            <button
                                style={{ ...styles.button, ...styles.successButton }}
                                onClick={handleUpdatePrefix}
                                disabled={!!walidujWzorzec(editingPrefix.format_pattern, editingPrefix.reset_period)}
                            >
                                ✓ Zapisz
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal tworzenia nowego */}
            {showCreateModal && (
                <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: '12px' }}>➕ Nowy prefiks</h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                style={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Lokalizacja *</label>
                                <select
                                    style={styles.input}
                                    value={newPrefix.location_id}
                                    onChange={(e) => {
                                        const locationId = e.target.value;
                                        setNewPrefix((poprzedni) => ({
                                            ...poprzedni,
                                            location_id: locationId,
                                            // Wzorzec z kodem oddzialu - taki sam jak zakladany
                                            // przez backend. Bez kodu numery z dwoch sklepow
                                            // moglyby sie zdublowac.
                                            format_pattern: formatRuszony
                                                ? poprzedni.format_pattern
                                                : wzorzecDomyslny(locationId)
                                        }));
                                    }}
                                >
                                    <option value="">Wybierz...</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.nazwa || loc.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Typ dokumentu *</label>
                                <select
                                    style={styles.input}
                                    value={newPrefix.document_type}
                                    onChange={(e) => setNewPrefix({ ...newPrefix, document_type: e.target.value })}
                                >
                                    <option value="">Wybierz...</option>
                                    {documentTypes.map(type => (
                                        <option key={type.type} value={type.type}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Prefiks *</label>
                                <input
                                    style={styles.input}
                                    value={newPrefix.prefix}
                                    onChange={(e) => setNewPrefix({ ...newPrefix, prefix: e.target.value })}
                                    placeholder="np. PAR"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Format numeru</label>
                                <input
                                    style={styles.input}
                                    value={newPrefix.format_pattern}
                                    onChange={(e) => {
                                        setFormatRuszony(true);
                                        setNewPrefix({ ...newPrefix, format_pattern: e.target.value });
                                    }}
                                    placeholder="{prefix}/{numer}/{miesiac}/{rok}"
                                />
                                <PodpowiedzWzorca
                                    wzorzec={newPrefix.format_pattern}
                                    prefiks={newPrefix.prefix}
                                    resetPeriod={newPrefix.reset_period}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Reset licznika</label>
                                <select
                                    style={styles.input}
                                    value={newPrefix.reset_period}
                                    onChange={(e) => setNewPrefix({ ...newPrefix, reset_period: e.target.value })}
                                >
                                    <option value="monthly">Miesięcznie</option>
                                    <option value="yearly">Rocznie</option>
                                    <option value="never">Nigdy</option>
                                </select>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Opis</label>
                                <input
                                    style={styles.input}
                                    value={newPrefix.description}
                                    onChange={(e) => setNewPrefix({ ...newPrefix, description: e.target.value })}
                                    placeholder="Opis"
                                />
                            </div>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                style={{ ...styles.button, border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
                                onClick={() => setShowCreateModal(false)}
                            >
                                Anuluj
                            </button>
                            <button
                                style={{ ...styles.button, ...styles.successButton }}
                                onClick={handleCreatePrefix}
                                disabled={
                                    !newPrefix.location_id
                                    || !newPrefix.document_type
                                    || !newPrefix.prefix
                                    || !!walidujWzorzec(newPrefix.format_pattern, newPrefix.reset_period)
                                }
                            >
                                ✓ Utwórz
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentPrefixesPage;
